import React, { useEffect, useState } from 'react';
import { EdgeProps, getBezierPath, useReactFlow, useStore } from '@xyflow/react';
import { CSSProperties } from 'react';

import useEdgeClick from '../../../hooks/useEdgeClick';
import styles from './EdgeTypes.module.css';
import '../../../styles/HoverInteractions.css';

type PriorityLevel = 'low' | 'medium' | 'high';

// All edges are gray - distinction is made by solid (necessary) vs dashed (sufficient)
const EDGE_COLOR = '#777777'; // Gray for all edges

export default function WorkflowEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  source,
  target,
  style,
  markerEnd,
  data
}: EdgeProps) {
  // see the hook for implementation details
  // onClick adds a node in between the nodes that are connected by this edge
  const onClick = useEdgeClick(id);
  
  // Hover state for smooth interactions
  const [isHovered, setIsHovered] = useState(false);
  
  // Use the target prop directly instead of trying to extract it from the ID
  const targetId = target;
  
  // Create a selector to watch for changes in the specific node
  // This will cause re-render whenever the target node's priority or necessity changes
  const targetNode = useStore((state) => {
    const node = state.nodeLookup.get(targetId);
    // Return both the node AND a cache-busting key so changes trigger re-renders
    return {
      node,
      // This key changes whenever priority or necessity changes, forcing edge color updates
      updateKey: `${node?.data?.priority || 'medium'}-${node?.data?.isNecessary || false}`
    };
  }, (a, b) => {
    // Custom equality function - only re-render if updateKey changes
    return a?.updateKey === b?.updateKey;
  });
  
  // Get necessity value from the target node (priority no longer affects edge color)
  const isNecessary = targetNode?.node?.data?.isNecessary || false;
  
  // All edges are gray, distinction by solid (necessary) vs dashed (sufficient)
  const customStyle: CSSProperties = {
    ...style,
    stroke: EDGE_COLOR, // All edges gray
    strokeWidth: isNecessary ? 3 : 2, // Thicker for necessary
    strokeDasharray: isNecessary ? 'none' : '5 5', // Solid for necessary, dashed for sufficient
  };

  const [edgePath, edgeCenterX, edgeCenterY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  return (
    <>
      <path
        id={id}
        style={customStyle}
        className={`${styles.edgePath} mindmap-edge`}
        d={edgePath}
        markerEnd={markerEnd}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      />
      <g 
        className={styles.edgeButtonGroup} 
        transform={`translate(${edgeCenterX}, ${edgeCenterY})`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <rect
          onClick={onClick}
          x={-10}
          y={-10}
          width={20}
          height={20}
          rx={3}
          ry={3}
          className={`${styles.edgeButton} hover-interactive`}
          style={{ 
            stroke: EDGE_COLOR, // Gray for consistency
            strokeWidth: isHovered ? 2 : 1,
            fill: 'var(--node-background)',
            opacity: isHovered ? 1 : 0,
            visibility: isHovered ? 'visible' : 'hidden'
          }}
        />
        <text 
          className={styles.edgeButtonText} 
          y={4} 
          x={-4} 
          style={{ 
            fill: EDGE_COLOR, // Gray for consistency
            fontSize: isHovered ? '15px' : '14px',
            fontWeight: 'bold',
            transition: 'font-size 0.15s ease',
            opacity: isHovered ? 1 : 0,
            visibility: isHovered ? 'visible' : 'hidden'
          }}
        >
          +
        </text>
      </g>
    </>
  );
} 